import { useEffect, useMemo, useRef, useState } from 'react';

import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { stripHtml } from './utils';
import { RICH_TEXT_HTML_CLASS } from './rich-text';
import { sentenceEndOffsets } from './sentence-offsets';

export interface ReadMoreTextProps {
    /** Plain text content (use `html` for rich content instead). */
    text?: string;
    /**
     * Rich-text (HTML) content, used in place of `text` when set. The crop is
     * measured with a Range over a single text node, so this is flattened to
     * plain text rather than rendered — the markup cannot survive a
     * character-offset slice. Callers wanting the formatting intact render the
     * full copy elsewhere and point Read more at it via `anchorHref`.
     */
    html?: string;
    /** Target line count for the collapsed summary (default 3). */
    lines?: number;
    /**
     * Crop to the first sentence that FILLS `lines` rather than the sentence
     * nearest to it, so the summary never comes up short of the target. Off by
     * default: the nearest-boundary crop reads better where a tight teaser
     * matters more than a filled block, and it cannot overshoot the way this
     * does when the sentence that reaches the target is a long one.
     */
    fillLines?: boolean;
    /** Class for the text itself. */
    className?: string;
    /** Class for the read-more/less toggle. */
    toggleClassName?: string;
    /**
     * When set, Read more is a link instead of an in-place expand — the text
     * stays cropped and the full copy lives at the target. An in-page anchor
     * (`#id`) smooth-scrolls to it (e.g. the product-details block); any other
     * href is a client-side route link (e.g. the professional's profile).
     */
    anchorHref?: string;
    /**
     * Replaces the default in-page anchor handling — for targets that need
     * more than a scroll (e.g. opening a collapsed accordion row first). The
     * handler owns preventDefault and the scroll.
     */
    onAnchorClick?: (event: React.MouseEvent) => void;
    /**
     * Keep the reader where they are when Read more is a route link.
     *
     * For a target on the same page under another tab — the profile's About
     * tab holds the full copy this block crops — the router's scroll reset
     * throws the reader to the top of a page they had already scrolled down.
     * Off by default: an anchor that leads to a different page entirely should
     * land at the top of it.
     */
    preventScrollReset?: boolean;
}

/**
 * Read more keeps the copy's size and weight — it continues the sentence rather
 * than sitting under it as a control — but rests a step down in grey so it
 * still separates from the sentence, and darkens to the copy's own ink on
 * hover. Consumers override the pair via `toggleClassName` per theme.
 */
const READ_MORE_CLASS =
    'cursor-pointer font-[inherit] text-[#8f8f8f] no-underline transition-colors hover:text-[#181818]';

/**
 * Shows the copy cropped to a whole sentence — never mid-word, and never with
 * an ellipsis — choosing whichever sentence ends nearest `lines`. That may land
 * a little under or over; a sentence cut in half to hit an exact line count
 * reads worse than one line too many.
 *
 * Read more follows inline as part of that last sentence, and the whole block
 * carries the link, so the target is the paragraph rather than two small words.
 *
 * Expanding in place swaps the flattened crop for the original markup, so the
 * full copy keeps the paragraph breaks and line breaks the CMS author wrote —
 * the crop has to flatten to slice at a character offset, but nothing past the
 * collapsed state does.
 */
export const ReadMoreText = ({
    text,
    html,
    lines = 3,
    fillLines = false,
    className,
    toggleClassName,
    anchorHref,
    onAnchorClick,
    preventScrollReset = false,
}: ReadMoreTextProps) => {
    const measureRef = useRef<HTMLParagraphElement>(null);
    const [expanded, setExpanded] = useState(false);
    // null until measured — until then the copy renders cropped by height, so
    // the first paint never flashes the full text.
    const [cropOffset, setCropOffset] = useState<number | null>(null);
    // Whether there is more copy than the space shows. Tracked separately from
    // the crop offset because the two can disagree: copy with no sentence
    // boundary to cut at overflows without the slice shortening it.
    const [overflows, setOverflows] = useState(false);

    const plainText = useMemo(
        () => (html ? stripHtml(html) : (text ?? '')),
        [html, text],
    );

    // The crop needs the rendered line boxes, so it cannot run until the client
    // has laid the copy out. This is what the server paints in the meantime:
    // the first whole sentence. It is usually short of the target, and the
    // measured crop replaces it on hydration — but it is never a sentence cut
    // in half, which a plain height clamp on this same copy would be.
    const firstBoundary = useMemo(
        () => sentenceEndOffsets(plainText)[0] ?? plainText.length,
        [plainText],
    );

    useEffect(() => {
        const el = measureRef.current;
        if (!el || !plainText) return;

        const measure = () => {
            const node = el.firstChild;
            if (!node) return;
            const range = document.createRange();
            const linesTo = (offset: number) => {
                range.setStart(node, 0);
                range.setEnd(node, offset);
                return range.getClientRects().length;
            };

            const total = linesTo(plainText.length);
            // Everything fits — no crop, and no Read more.
            if (total <= lines) {
                setCropOffset(plainText.length);
                setOverflows(false);
                return;
            }

            const offsets = sentenceEndOffsets(plainText);
            let best = plainText.length;
            if (fillLines) {
                // Offsets ascend and the line count rises with them, so the
                // first boundary to reach the target is the shortest crop that
                // fills it. None reaching it means no sentence break fills the
                // target, and the whole copy stands.
                best =
                    offsets.find((offset) => linesTo(offset) >= lines) ??
                    plainText.length;
            } else {
                let bestDistance = Number.POSITIVE_INFINITY;
                for (const offset of offsets) {
                    const distance = Math.abs(linesTo(offset) - lines);
                    // Strictly-less keeps the earliest boundary on a tie, so a
                    // tie resolves to the shorter summary.
                    if (distance < bestDistance) {
                        bestDistance = distance;
                        best = offset;
                    }
                }
            }
            setCropOffset(best);
            setOverflows(true);
        };

        measure();
        document.fonts?.ready.then(measure).catch(() => {});
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [plainText, lines, fillLines]);

    // There is more to read: what Read more is offered for. Before the first
    // measurement that is whatever the opening-sentence crop leaves behind.
    const isCropped =
        cropOffset === null ? firstBoundary < plainText.length : overflows;
    // Whether the slice itself shortened the copy. Copy that never terminates —
    // no full stop, or a bullet list whose items flatten to a single run — has
    // no boundary before the end, so it overflows with nothing trimmed.
    const sentenceCropped =
        cropOffset !== null && cropOffset < plainText.length;
    const visibleText = expanded
        ? plainText
        : plainText.slice(0, cropOffset ?? firstBoundary).trimEnd();

    // Bounded by height rather than `-webkit-line-clamp`: the clamp paints an
    // ellipsis, which is the very thing the crop exists to avoid. Only for copy
    // the slice could not shorten — no full stop anywhere, or a bullet list
    // that flattens to one run — which would otherwise render in full,
    // unbounded. Everything else is already cut at a sentence, before and after
    // measuring, so it needs no ceiling.
    const unshortenable =
        cropOffset === null
            ? firstBoundary >= plainText.length
            : overflows && !sentenceCropped;
    const clampStyle =
        !expanded && unshortenable
            ? ({ maxHeight: `${lines}lh`, overflow: 'hidden' } as const)
            : undefined;

    const handleAnchorClick = (event: React.MouseEvent) => {
        if (!anchorHref?.startsWith('#')) return;
        const target = document.getElementById(anchorHref.slice(1));
        if (!target) return;
        event.preventDefault();
        const reduceMotion = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        target.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    // With an anchor the whole block is already a link, so the affordance is
    // inert text inside it — a control nested in a link is neither valid nor
    // reachable. Expanding in place has no such wrapper, so there it has to BE
    // the control: a span with a click handler is invisible to the keyboard and
    // announces nothing, which is what this was.
    const toggleLabel = (label: string) =>
        anchorHref ? (
            <span className={cn(READ_MORE_CLASS, toggleClassName)}>
                {label}
            </span>
        ) : (
            <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                className={cn(
                    READ_MORE_CLASS,
                    'bg-transparent p-0',
                    toggleClassName,
                )}
            >
                {label}
            </button>
        );

    const body = (
        <>
            {visibleText}{' '}
            {isCropped && !expanded ? toggleLabel('Read more') : null}
            {isCropped && expanded && !anchorHref
                ? toggleLabel('Read less')
                : null}
        </>
    );

    return (
        // relative so the measuring twin below can span this exact width.
        <div className="relative">
            {/*
                Hidden twin holding the full copy in one text node: the crop is
                measured with a Range over it, which needs the real column width
                and type. `inset-x-0` is load-bearing — an absolute block with
                no width shrink-wraps, and every line break would be measured
                against the wrong column. aria-hidden and out of flow, so it
                neither reads nor takes space.
            */}
            <p
                ref={measureRef}
                aria-hidden="true"
                className={cn(
                    'pointer-events-none absolute inset-x-0 top-0 -z-10 m-0 opacity-0 select-none',
                    className,
                )}
            >
                {plainText}
            </p>

            {expanded && html ? (
                // Expanded: the author's markup, not the flattened run the
                // crop measured against, so paragraphs and line breaks survive.
                <>
                    <div
                        className={cn('m-0', className, RICH_TEXT_HTML_CLASS)}
                        dangerouslySetInnerHTML={{ __html: html }}
                    />
                    {toggleLabel('Read less')}
                </>
            ) : isCropped && anchorHref ? (
                anchorHref.startsWith('#') ? (
                    <a
                        href={anchorHref}
                        onClick={onAnchorClick ?? handleAnchorClick}
                        className={cn('m-0 block no-underline', className)}
                    >
                        {body}
                    </a>
                ) : (
                    <RoccoLink
                        to={anchorHref}
                        preventScrollReset={preventScrollReset}
                        className={cn('m-0 block no-underline', className)}
                    >
                        {body}
                    </RoccoLink>
                )
            ) : isCropped ? (
                <p className={cn('m-0', className)} style={clampStyle}>
                    {body}
                </p>
            ) : (
                <p className={cn('m-0', className)} style={clampStyle}>
                    {visibleText}
                </p>
            )}
        </div>
    );
};
